import { t, formatDate } from "../core/i18n.js";
import { icon } from "./icons.js";

const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const names = [
  "Go for a run",
  "Focus on important work",
  "Learn something new",
  "Plan tomorrow",
];
const head = (glyph, title, extra = "") =>
  `<div class="product-window-head">${icon(glyph)}<b>${t(title)}</b><span>${extra || "DayX"}</span><i></i><i></i><i></i></div>`;

function focusScene() {
  return `${head("pomodoro", "Pomodoro")}<div class="product-focus-body"><div class="product-focus-dial"><svg viewBox="0 0 220 220"><circle cx="110" cy="110" r="100"/><circle class="product-focus-ring" cx="110" cy="110" r="100"/></svg><div><small>${t("FOCUS TIME")}</small><b data-product-time>25:00</b><span>${t("Just this moment. Just this task.")}</span></div></div><div class="product-focus-sequence"><span>${t("Session sequence")}</span><div><small>${t("Work")}</small><b>25 <i>${t("min")}</i></b></div><div><small>${t("Rest")}</small><b>5 <i>${t("min")}</i></b></div><div><small>${t("Repeats")}</small><b>4 <i>×</i></b></div></div></div><div class="product-focus-task">${icon("edit")}<span>${t("Focus on important work")}</span><div data-product-start>${icon("play")}<b data-product-start-label>${t("START")}</b></div></div>`;
}
function tableScene() {
  return `${head("table", "30-day plan table")}<div class="product-table-body"><div class="product-month-tabs">${["1–7", "8–14", "15–21", "22–30"].map((n, i) => `<span data-product-week="${i}" class="${i === 0 ? "selected" : ""}">${n}</span>`).join("")}</div><div class="product-plan-table"><span>${t("Plan")}</span>${days.map((day, i) => `<div class="product-table-day"><b data-product-day="${i}">${i + 1}</b><small>${t(day)}</small></div>`).join("")}${names
    .slice(0, 3)
    .map(
      (name, row) =>
        `<div class="product-table-name">${t(name)}</div>${days.map((_, column) => `<span data-product-cell="${row * 7 + column}">${icon("check")}</span>`).join("")}`,
    )
    .join(
      "",
    )}</div><div class="product-table-summary"><span>${icon("check")}${t("Small steps add up.")}</span><b data-product-total>0 / 21</b></div></div>`;
}
function weeklyScene() {
  return `${head("weekly", "Weekly overview")}<div class="product-weekly-body"><div class="product-weekly-stats"><div><small>${t("Weekly completion")}</small><b>71<span>%</span></b></div><div><small>${t("Completed")}</small><b>20<span> / 28</span></b></div><span>${icon("all")}</span></div><div class="product-weekly-chart">${[2, 3, 4, 2, 4, 3, 2].map((count, i) => `<div data-product-review="${i}" style="--height:${count * 25}%;--delay:${i * 0.12}s"><i></i><b>${count}/4</b><span>${t(days[i])}</span></div>`).join("")}</div><div class="product-weekly-day"><b data-product-review-title>${t("Mon")}</b><span data-product-review-total>${t("{n} of {total} complete", { n: 2, total: 4 })}</span></div><div class="product-weekly-plans">${names.map((name, i) => `<div data-product-review-plan="${i}"><span>${icon("check")}</span><b class="demo-task-name">${t(name)}</b></div>`).join("")}</div></div>`;
}
function examScene() {
  const date = new Date();
  date.setDate(date.getDate() + 14);
  const month = new Date(date.getFullYear(), date.getMonth(), 1);
  const count = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  const offset = (month.getDay() + 6) % 7;
  const cells = Math.ceil((offset + count) / 7) * 7;
  return `${head("exams", "Exams")}<div class="product-exam-body"><div class="product-calendar"><div class="product-calendar-heading">${icon("today")}<b>${formatDate(date, { month: "long", year: "numeric" })}</b></div><div class="product-calendar-grid">${days.map((day) => `<small>${t(day)}</small>`).join("")}${Array.from(
    { length: cells },
    (_, i) => {
      const day = i - offset + 1;
      return day > 0 && day <= count
        ? `<span ${day === date.getDate() ? "data-product-exam-day" : ""}>${day}</span>`
        : "<i></i>";
    },
  ).join(
    "",
  )}</div></div><div class="product-exam-editor"><small>${t("Add exam")}</small><div class="product-exam-name"><span data-product-exam-typing></span><i></i></div><div class="product-exam-date">${icon("today")}<span>${formatDate(date, { day: "numeric", month: "short", year: "numeric" })}</span>${icon("check")}</div><div class="product-exam-save" data-product-exam-save>${t("Save")}${icon("arrow")}</div><div class="product-exam-result"><span>${icon("check")}${t("English exam")}</span><b>14<small>${t("days left")}</small></b></div></div></div><div class="product-exam-bottom">${icon("check")}<span>${t("Saved. Keep the date in sight.")}</span></div>`;
}

export function productShowcase(cursor) {
  const features = [
    [
      "focus",
      "Pomodoro",
      "Make room for focus.",
      "Start a focus session, take a break and keep a steady rhythm.",
      [
        "Set your session length",
        "Start with one task",
        "Take a break when it is time",
      ],
      "pomodoro",
      focusScene,
    ],
    [
      "table",
      "Plan table",
      "A clear view of your month.",
      "Keep your routines in one table. Check off completed plans and move between weeks to see how your month is going.",
      [
        "See your plans by day",
        "Mark what you finish",
        "Keep your progress in sight",
      ],
      "table",
      tableScene,
    ],
    [
      "weekly",
      "Weekly overview",
      "See what worked this week.",
      "Review your week at a glance. Choose a day to see your completed plans and decide what to focus on next.",
      [
        "Compare your days",
        "Review completed plans",
        "Make room for the next step",
      ],
      "weekly",
      weeklyScene,
    ],
    [
      "exams",
      "Exam dates",
      "Prepare with a date in mind.",
      "Add the exam name and choose a date. DayX shows how many days remain, so you can plan your preparation.",
      [
        "Add an important date",
        "See the days remaining",
        "Prepare at your own pace",
      ],
      "exams",
      examScene,
    ],
  ];
  return `<nav class="product-jump-links" aria-label="${t("Explore the demos")}">${features.map(([kind, label]) => `<a href="#demo-${kind}">${t(label)}${icon("arrow")}</a>`).join("")}</nav><div class="product-showcase">${features.map(([kind, label, title, description, steps, route, scene], i) => `<section class="product-story ${i % 2 ? "reverse" : ""}" id="demo-${kind}" aria-labelledby="demo-${kind}-title"><div class="product-story-copy"><div class="landing-eyebrow">0${i + 1} / ${t(label)}</div><h3 id="demo-${kind}-title">${t(title)}</h3><p>${t(description)}</p><ol>${steps.map((step, j) => `<li><span>0${j + 1}</span>${t(step)}</li>`).join("")}</ol><a href="/${route}" data-enter="${route}">${t("Try it in DayX")}${icon("arrow")}</a></div><div class="product-scene ${kind === "focus" ? "dark" : ""}" data-demo="product-${kind}" aria-hidden="true">${scene()}${cursor}</div></section>`).join("")}</div>`;
}

export function drawProductDemo(
  scene,
  settled,
  { setText, formatTime, moveCursor },
) {
  const el = scene.element,
    kind = el.dataset.demo,
    seconds = (scene.elapsed % 18000) / 1000;
  if (kind === "product-focus") {
    const running = !settled && seconds >= 3;
    el.classList.toggle("focus-running", running);
    if (scene.running !== running) {
      scene.running = running;
      el.querySelector("[data-product-start] .ui-icon").outerHTML = icon(
        running ? "pause" : "play",
      );
    }
    setText(
      el.querySelector("[data-product-time]"),
      formatTime(1500 - (running ? Math.floor(seconds - 3) : 0)),
    );
    setText(
      el.querySelector("[data-product-start-label]"),
      t(running ? "PAUSE" : "START"),
    );
    moveCursor(
      scene,
      el.querySelector("[data-product-start]"),
      0,
      !settled && seconds < 10,
      seconds >= 3 && seconds < 3.4,
    );
  } else if (kind === "product-table") {
    const week = !settled && seconds >= 9 ? 1 : 0,
      phase = week ? seconds - 9 : seconds;
    const complete = settled
      ? 14
      : Math.min(21, Math.floor(Math.max(0, phase - 1) * 2.7));
    el.querySelectorAll("[data-product-week]").forEach((tab, i) =>
      tab.classList.toggle("selected", i === week),
    );
    el.querySelectorAll("[data-product-day]").forEach((day, i) =>
      setText(day, String(week * 7 + i + 1)),
    );
    el.querySelectorAll("[data-product-cell]").forEach((cell, i) =>
      cell.classList.toggle("done", i < complete),
    );
    setText(el.querySelector("[data-product-total]"), `${complete} / 21`);
    const switching = !settled && seconds >= 8.2 && seconds < 9.6;
    moveCursor(
      scene,
      el.querySelector(
        switching
          ? '[data-product-week="1"]'
          : `[data-product-cell="${Math.min(complete, 20)}"]`,
      ),
      switching ? "week" : `${week}:${complete}`,
      !settled && seconds < 16,
      switching
        ? seconds >= 9
        : complete > 0 && (Math.max(0, phase - 1) * 2.7) % 1 < 0.4,
    );
  } else if (kind === "product-weekly") {
    const selected = settled ? 4 : seconds >= 11 ? 4 : seconds >= 6 ? 2 : 0;
    const count = [2, 3, 4, 2, 4, 3, 2][selected];
    el.querySelectorAll("[data-product-review]").forEach((day, i) =>
      day.classList.toggle("selected", i === selected),
    );
    el.querySelectorAll("[data-product-review-plan]").forEach((row, i) =>
      row.classList.toggle("done", i < count),
    );
    setText(el.querySelector("[data-product-review-title]"), t(days[selected]));
    setText(
      el.querySelector("[data-product-review-total]"),
      t("{n} of {total} complete", { n: count, total: 4 }),
    );
    moveCursor(
      scene,
      el.querySelector(`[data-product-review="${selected}"]`),
      selected,
      !settled && seconds < 15,
      [1, 6, 11].some((n) => seconds >= n && seconds < n + 0.4),
    );
  } else if (kind === "product-exams") {
    const name = t("English exam"),
      saved = settled || seconds >= 5;
    setText(
      el.querySelector("[data-product-exam-typing]"),
      settled
        ? name
        : name.slice(0, Math.floor(Math.max(0, seconds - 0.5) * 9)),
    );
    el.classList.toggle("date-selected", settled || seconds >= 2.5);
    el.classList.toggle("exam-saved", saved);
    const cue = seconds >= 3.5 ? 1 : 0;
    moveCursor(
      scene,
      el.querySelector(
        cue ? "[data-product-exam-save]" : "[data-product-exam-day]",
      ),
      cue,
      !settled && seconds < 8,
      [2.5, 5].some((n) => seconds >= n && seconds < n + 0.4),
    );
  }
}
